/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ItemInfo, Bounds } from '../Diagram.types';
import type { Ref } from 'preact/hooks';
import { calculateOffset, useDatatip } from '../../hooks/PRIVATE_useDatatip';
import type { Pan } from '../utils/panUtils';
import { useUser } from '../../hooks/UNSAFE_useUser';

type props<K1, K2> = {
  text?: string;
  focusedItemInfo: ItemInfo<K1, K2>;
  focusedItemBoundsRef: Ref<Bounds | null>;
  panState: Pan;
  zoom: number;
  width: number;
};

/**
 * Returns the datatip.
 * @param text The text string for the diagram item.
 * @param rootRef The ref for root of the diagram.
 * @param focusedItemBoundsRef The ref for focused item bounds.
 * @param focusedItemInfo The ItemInfo for focused item.
 * @returns
 */
export const useDiagramDatatip = <K1, K2>({
  text,
  focusedItemBoundsRef,
  focusedItemInfo,
  panState,
  zoom,
  width
}: props<K1, K2>) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const elementBounds = focusedItemInfo?.isCurrent
    ? {
        x: focusedItemBoundsRef.current!.x * zoom + panState.panX,
        y: focusedItemBoundsRef.current!.y * zoom + panState.panY,
        width: focusedItemBoundsRef.current!.w * zoom,
        height: focusedItemBoundsRef.current!.h * zoom
      }
    : undefined;

  const { datatipContent, datatipProps } = useDatatip({
    content: text,
    placement: 'top-start',
    offset: calculateOffset(isRtl, width, elementBounds),
    anchor: focusedItemInfo?.isCurrent ? 'element' : 'pointer'
  });
  return { datatipContent: datatipContent, datatipProps: datatipProps };
};
